import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AppLayout } from '../components/layout';
import { Card, Button, Badge, Input } from '../components/ui';
import useLocationsStore from '../stores/locationsStore';

const Locations = () => {
	const navigate = useNavigate();
	const { locations, deleteLocation } = useLocationsStore();
	const [searchTerm, setSearchTerm] = useState('');

	const filteredLocations = locations.filter(
		location =>
			location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
			location.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
			location.address.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleDelete = (id: string, name: string) => {
		if (confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
			deleteLocation(id);
			toast.success('Location deleted successfully!');
		}
	};

	return (
		<AppLayout>
			{/* Header */}
			<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
				<div>
					<h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
						All Locations
					</h1>
				</div>
				<Button
					variant="primary"
					onClick={() => navigate('/locations/new')}
					className="w-full sm:w-auto"
				>
					+ Add New Location
				</Button>
			</div>

			{/* Search Bar */}
			<div className="mb-6">
				<Input
					type="text"
					placeholder="Search locations..."
					value={searchTerm}
					onChange={e => setSearchTerm(e.target.value)}
					className="w-full sm:max-w-md"
				/>
			</div>

			{/* Locations List */}
			<div className="space-y-4">
				{filteredLocations.length === 0 ? (
					<Card>
						<div className="text-center py-12">
							<div className="text-6xl mb-4 opacity-30">📍</div>
							<h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
								{searchTerm ? 'No locations found' : 'No locations yet'}
							</h3>
							<p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
								{searchTerm
									? 'Try a different search term'
									: 'Get started by adding your first location'}
							</p>
							{!searchTerm && (
								<Button
									variant="primary"
									onClick={() => navigate('/locations/new')}
								>
									+ Add First Location
								</Button>
							)}
						</div>
					</Card>
				) : (
					filteredLocations.map(location => (
						<Card key={location.id} className="hover:shadow-lg transition-shadow">
							<div className="flex flex-col sm:flex-row justify-between items-start gap-4">
								<div className="flex-1 min-w-0 w-full sm:w-auto">
									<div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
										<h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 truncate">
											{location.name}
										</h3>
										<Badge
											variant={
												location.status === 'active' ? 'success' : 'warning'
											}
										>
											{location.status === 'active' ? 'Active' : 'Inactive'}
										</Badge>
									</div>
									<p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4 truncate">
										{location.businessName}
									</p>

									<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">
												Address
											</div>
											<div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
												{location.address}
												<br />
												{location.city}, {location.state} {location.zipCode}
											</div>
										</div>
										<div>
											<div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide font-semibold mb-1">
												Contact
											</div>
											<div className="text-xs sm:text-sm text-gray-900 dark:text-gray-100">
												{location.phone}
												<br />
												{location.email}
											</div>
										</div>
									</div>
								</div>

								<div className="flex flex-row sm:flex-col gap-2 w-full sm:w-auto">
									<Button
										size="sm"
										variant="secondary"
										onClick={() => navigate(`/locations/${location.id}/edit`)}
										className="flex-1 sm:flex-none"
									>
										✏️ Edit
									</Button>
									<Button
										size="sm"
										variant="danger"
										onClick={() => handleDelete(location.id, location.name)}
										className="flex-1 sm:flex-none"
									>
										🗑️ Delete
									</Button>
								</div>
							</div>
						</Card>
					))
				)}
			</div>

			<div className="mt-8 pt-5 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400 text-center">
				© 2025 Beauty Business Portal. All rights reserved.
			</div>
		</AppLayout>
	);
};

export default Locations;
